import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth";
import { useConfirmEmailVerify } from "@/lib/email-verify";

const AUTH_APP_URL =
  (import.meta.env.VITE_AUTH_APP_URL as string | undefined) ??
  "http://localhost:5174";

const AUTO_REDIRECT_MS = 1500;
const WATCHDOG_MS = 10_000;

/**
 * Module-level set survives the StrictMode mount → unmount → remount
 * cycle in development (refs do not). One verify per token per browser
 * session — a hard refresh resets the set, so a deliberately re-tried
 * link still works.
 */
const submittedTokens = new Set<string>();

/**
 * Landing page for the email-verification magic link
 * (`/verify-email?token=…`). Reads the token from the URL, fires a
 * single confirm call, and renders the right state. Works whether or
 * not the visitor has a session — the backend confirm endpoint
 * doesn't require a Bearer (validates via the magic_link row).
 */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const confirm = useConfirmEmailVerify();

  // Watchdog: if the mutation hasn't settled within 10s, flip into a
  // "stuck" state so the user gets actionable copy instead of an
  // indefinite spinner.
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!token || submittedTokens.has(token)) return;
    submittedTokens.add(token);
    confirm.mutate(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!confirm.isPending) return;
    const id = window.setTimeout(() => setStuck(true), WATCHDOG_MS);
    return () => window.clearTimeout(id);
  }, [confirm.isPending]);

  // The `already_used` case is functionally a success from the user's
  // POV — their email IS verified (or was already verified). Treat it
  // as success-with-an-asterisk rather than as a hard error.
  const alreadyVerified =
    confirm.isError &&
    (confirm.error as Error).message?.includes("already_used");

  const isSuccess = confirm.isSuccess || alreadyVerified;

  // Auto-redirect after success (signed-in users only — signed-out
  // visitors need to click through to the auth subapp).
  useEffect(() => {
    if (!isSuccess || !accessToken) return;
    const id = window.setTimeout(
      () => navigate("/", { replace: true }),
      AUTO_REDIRECT_MS,
    );
    return () => window.clearTimeout(id);
  }, [isSuccess, accessToken, navigate]);

  // ---- empty / missing token --------------------------------------
  if (!token) {
    return (
      <Layout>
        <Icon variant="error">
          <X className="h-8 w-8" />
        </Icon>
        <Title>Missing verification token</Title>
        <Subtitle>
          The link you used doesn't include a verification token. Open
          the link from your email again, or request a new
          verification email from the dashboard.
        </Subtitle>
        <Cta accessToken={accessToken} onDashboard={() => navigate("/")} />
      </Layout>
    );
  }

  // ---- watchdog "stuck" state -------------------------------------
  if (stuck && confirm.isPending) {
    return (
      <Layout>
        <Icon variant="error">
          <X className="h-8 w-8" />
        </Icon>
        <Title>This is taking longer than expected</Title>
        <Subtitle>
          We couldn't reach the verification server. Check your
          connection and try the link again, or sign in and click
          Resend on the verification banner.
        </Subtitle>
        <Cta accessToken={accessToken} onDashboard={() => navigate("/")} />
      </Layout>
    );
  }

  // ---- pending ----------------------------------------------------
  if (confirm.isPending || confirm.isIdle) {
    return (
      <Layout>
        <Icon variant="muted">
          <Loader2 className="h-8 w-8 animate-spin" />
        </Icon>
        <Title>Verifying your email…</Title>
        <Subtitle>This will only take a moment.</Subtitle>
      </Layout>
    );
  }

  // ---- success (incl. already_used → friendly success) ------------
  if (isSuccess) {
    return (
      <Layout>
        <Icon variant="success">
          <Check className="h-8 w-8" />
        </Icon>
        <Title>Email verified</Title>
        <Subtitle>
          {alreadyVerified
            ? "This link had already been used — your email is already on file."
            : "Your email is confirmed. You can manage bookings and accept payments now."}
        </Subtitle>
        {accessToken ? (
          <p className="text-xs text-muted-foreground">
            Continuing to dashboard…
          </p>
        ) : null}
        <Cta accessToken={accessToken} onDashboard={() => navigate("/")} />
      </Layout>
    );
  }

  // ---- genuine error ----------------------------------------------
  const raw = (confirm.error as Error).message ?? "";
  const friendly = mapError(raw);
  return (
    <Layout>
      <Icon variant="error">
        <X className="h-8 w-8" />
      </Icon>
      <Title>{friendly.title}</Title>
      <Subtitle>{friendly.body}</Subtitle>
      <Cta accessToken={accessToken} onDashboard={() => navigate("/")} />
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/* presentation helpers                                               */
/* ------------------------------------------------------------------ */

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center px-6">
      <div className="flex flex-col items-center gap-5 text-center max-w-md">
        {children}
      </div>
    </div>
  );
}

function Icon({
  variant,
  children,
}: {
  variant: "muted" | "success" | "error";
  children: React.ReactNode;
}) {
  const cls =
    variant === "success"
      ? "bg-primary text-primary-foreground"
      : variant === "error"
        ? "bg-destructive text-destructive-foreground"
        : "bg-muted text-muted-foreground";
  return (
    <div
      className={`h-16 w-16 rounded-full inline-flex items-center justify-center ${cls}`}
    >
      {children}
    </div>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="text-[24px] font-bold tracking-tight">{children}</h1>
  );
}

function Subtitle({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function Cta({
  accessToken,
  onDashboard,
}: {
  accessToken: string | null;
  onDashboard: () => void;
}) {
  if (accessToken) {
    return (
      <Button onClick={onDashboard} className="gap-2">
        Continue to dashboard <ArrowRight className="h-4 w-4" />
      </Button>
    );
  }
  return (
    <Button asChild>
      <a href={`${AUTH_APP_URL}/sign-in`}>Sign in to continue</a>
    </Button>
  );
}

/**
 * Map backend `message` codes (now surfaced cleanly via
 * `useConfirmEmailVerify`) to operator-facing copy. `already_used`
 * is handled at a higher level as a success-equivalent; the rest are
 * genuine errors.
 */
function mapError(raw: string): { title: string; body: string } {
  if (raw.includes("expired") || raw.includes("invalid_or_expired")) {
    return {
      title: "Link expired",
      body:
        "This verification link is no longer valid. Sign in and click Resend on the verification banner to get a new one.",
    };
  }
  if (raw.includes("wrong_purpose")) {
    return {
      title: "Wrong link",
      body:
        "This link wasn't an email-verification link. Try resending the verification email from the dashboard.",
    };
  }
  if (raw === "verify_failed" || !raw) {
    return {
      title: "Couldn't reach the server",
      body:
        "We couldn't reach the verification server. Check your connection and try the link again.",
    };
  }
  return {
    title: "Couldn't verify your email",
    body: raw,
  };
}
